/**
 * Email Icon Field is an email type input element
 * with the email or warning icon on the left side
 * and edit icon on the right side
 * Depending on the property combinations passed the view should adjust
 * This can be read-only (disabled) and editable
 * states: warning (required, empty value, read-only), edit invalid email, edit valid email, disabled
 */

import { KEY_CODE } from 'Common/constants/keyCode';
import { isValidEmailAddress } from 'Common/utilities/string';

export default class EmailIconFieldCtrl {
  constructor($timeout) {
    'ngInject';

    this.$timeout = $timeout;
  }

  $onInit() {
    this.escapeKeyCode = KEY_CODE.ESC;
    this.setWarningState(false);
    if (this.isFastLoad) {
      this.resetField();
      return;
    }
    this.resetTimeout = this.$timeout(() => this.resetField(), 2000);
  }

  $onDestroy() {
    this.$timeout.cancel(this.resetTimeout);
    this.labelTimeout && this.$timeout.cancel(this.labelTimeout);
  }

  $onChanges(props) {
    if (props.emailModel && props.id) {
      this.initEmailModel = this.emailModel;
      this.resetField();
    }
  }

  resetField(includeEditState = true) {
    includeEditState && this.setEditState(false);
    this.setSavingState(false);
    this.setInvalidState(false);
    this.showWarning();
    this.showDisabled();
    this.setConfirmPopover(false);
    this.updateParentState();
  }

  setDisabledState(state) {
    this.isDisabledState = state;
  }

  setWarningState(state) {
    this.isWarningState = state;
  }

  setInvalidState(state) {
    this.isInvalidState = state;
  }

  setSavingState(state) {
    this.isSavingState = state;
  }

  setConfirmPopover(state) {
    this.showConfirmPopover = state;
  }

  setEditState(state) {
    this.isEdit = state;
    this.setDisabledState(!state);
    this.updateParentState();
  }

  getOpenState() {
    return this.isEdit || this.isInvalidState || this.isWarningState;
  }

  showWarning() {
    this.setWarningState(!!this.isRequired && !this.emailModel);
  }

  showDisabled() {
    this.setDisabledState(!this.isEdit && !this.isWarningState);
  }

  isValid(event, isKeyEvent = false) {
    if (event.keyCode === KEY_CODE.ESC) {
      this.cancel();
      event.preventDefault();
      return;
    }
    this.isKeyEvent = isKeyEvent;
    this.setEditState(true);
    const element = event.currentTarget;
    const { classList } = element;
    this.setInvalidState(
      (element && classList.contains('ng-invalid-email')) || false,
    );
    this.setWarningState(
      !!this.isRequired && !this.emailModel && !this.isInvalidState,
    );

    if (this.emailModel) {
      this.setInvalidState(!isValidEmailAddress(this.emailModel));
    }

    this.updateParentState();
  }

  setDataSavedState() {
    this.dataIsSaved = !this.dataIsSaved;
  }

  editMode() {
    this.setDisabledState(false);
  }

  onRemoveFocus() {
    const isNotPristine =
      (this.emailModel && this.emailModel.trim()) !== this.initEmailModel &&
      this.isEdit &&
      !this.isInvalidState &&
      !this.isWarningState;

    if (isNotPristine) {
      !this.showConfirmPopover && this.setConfirmPopover(true);
      this.onFocusOut && this.onFocusOut();
    } else {
      this.cancel();
    }
  }

  save() {
    this.setSavingState(true);
    return this.onSave({
      email: this.emailModel,
      id: this.id,
    })
      .then(() => {
        this.initEmailModel = this.emailModel;
        this.setDataSavedState();
        this.resetField(false);
        this.labelTimeout = this.$timeout(() => {
          this.setDataSavedState();
          this.setEditState(false);
        }, 1000);
        this.updateParentState();
      })
      .catch(() => this.cancel());
  }

  cancel(reset = true) {
    reset && (this.emailModel = this.initEmailModel);
    this.resetField();
    this.setConfirmPopover(false);
    this.updateParentState();
  }

  updateParentState() {
    this.onUpdate({
      email: this.emailModel,
      isEmailEdit: this.getOpenState(),
      cancelCallback: () => {
        this.cancel();
      },
    });
  }

  onRemovePopoverFocus(event) {
    if (event.keyCode === this.escapeKeyCode) {
      event.preventDefault();
    }
  }
}
